import type { KyInstance } from 'ky'
import {
  failureCode,
  readFailureTag,
  tagFailure,
  type Failure,
} from './failure'
import {
  AfterPaymentError,
  NativeStoreError,
  PromotionalOfferSignatureError,
  PurchaseConfirmationPendingError,
  StaleTransactionError,
  SubscriptionConflictError,
  subscribe as subscribeCore,
  type FetchServiceLevelStatus,
  type ReadServiceLevel,
  type ServiceLevelStatus,
  type SubscribeOffer,
  type SubscribeOptions,
  type SubscriptionServiceLevelStatus,
} from './subscribe'
import { restoreSubscription, type RestoreSubscriptionOptions } from './restore'
import type { PaymentPlatform, ProductMetadata } from './types'

/**
 * A classified, framework-agnostic description of why a purchase failed.
 *
 * `classifyPurchaseError` turns a raw thrown error into one of these intents;
 * a per-app `PurchasePresenter` decides how to render each one. `AbortError`
 * is deliberately not part of this union — it is a no-op handled by the caller.
 */
export type PurchaseErrorIntent =
  | { kind: 'payment_timeout'; detail: string; failure: Failure }
  | { kind: 'illegal_transaction_restore'; failure: Failure }
  | { kind: 'http_error'; status: number; failure: Failure }
  | { kind: 'no_internet'; failure: Failure }
  /**
   * Another Forest account owns the subscription (409/412). `ownerUid` is
   * that account, or `null` when the Receipt System named none.
   */
  | { kind: 'subscription_conflict'; ownerUid: number | null; failure: Failure }
  /**
   * A payment may have succeeded but is not yet confirmed: show the purchase
   * confirmation dialog, never an error (GLOSSARY.md).
   */
  | { kind: 'confirmation_pending'; failure: Failure }
  | { kind: 'unknown'; failure: Failure }

export function classifyPurchaseError(error: unknown): PurchaseErrorIntent {
  // It carries its own failure, whatever step a wrapper tagged it with.
  if (error instanceof PurchaseConfirmationPendingError) {
    return { kind: 'confirmation_pending', failure: error.failure }
  }
  const { step, cause, purchaseId, transactionId } = readFailureTag(
    error,
    'purchase',
  )
  const failure: Failure = {
    code: failureCode(step, cause),
    ...(purchaseId !== undefined && { purchaseId }),
  }
  if (error instanceof NativeStoreError) {
    return { kind: 'payment_timeout', detail: error.message, failure }
  }
  if (error instanceof AfterPaymentError) {
    // The error carries its own IDs, for one no step tagged (the debug one).
    const orderTransactionId = transactionId ?? error.transactionId
    return {
      kind: 'illegal_transaction_restore',
      failure: {
        ...failure,
        purchaseId: purchaseId ?? error.purchaseId,
        ...(orderTransactionId != null && {
          transactionId: orderTransactionId,
        }),
      },
    }
  }
  // No payment was taken, so no "payment successful, tap Restore" dialog, and
  // its 410 is not an order error either (ADR 0045).
  if (error instanceof StaleTransactionError) {
    return { kind: 'unknown', failure }
  }
  // Checked before the HTTP-status branch, which its 409/412 would match.
  if (error instanceof SubscriptionConflictError) {
    return {
      kind: 'subscription_conflict',
      ownerUid: error.ownerUid,
      failure,
    }
  }
  // The cause, read before step telemetry renamed the error, decides these.
  if (cause === 'timeout' || cause === 'network') {
    return { kind: 'no_internet', failure }
  }
  // A failed signature is never an order error, whatever its status.
  if (error instanceof PromotionalOfferSignatureError) {
    return { kind: 'unknown', failure }
  }
  const status = httpStatusOf(cause)
  if (status !== undefined) {
    return { kind: 'http_error', status, failure }
  }
  return { kind: 'unknown', failure }
}

function httpStatusOf(cause: string): number | undefined {
  const match = /^http_(\d+)$/.exec(cause)
  return match ? Number(match[1]) : undefined
}

/**
 * The handful of facts every app needs from a completed purchase to drive its
 * own success side-effects (analytics labels, reminder dialogs, store sets).
 * Derived purely from the polled service-level status and the purchased product.
 */
export interface SuccessFacts {
  /** Whether the poll resolved to an active subscription (vs. null/timeout). */
  isSubscription: boolean
  /** Whether the resolved subscription is currently in its trial period. */
  isTrialSuccess: boolean
  /** The subscription's expiry, when known — used to schedule reminders. */
  expiresAt?: string
  /** The purchased product's store SKU. */
  sku: string
}

export function deriveSuccessFacts(
  status: SubscriptionServiceLevelStatus | null,
  product: ProductMetadata,
): SuccessFacts {
  const isSubscription = status?.service_level === 'subscription'
  return {
    isSubscription,
    isTrialSuccess: isSubscription && status.subscription.is_in_trial,
    expiresAt: isSubscription ? status.subscription.expires_at : undefined,
    sku: product.sku,
  }
}

/** An app's native-bridge bindings, as the flow needs them. */
export interface SubscribeFlowNativeBindings<Product extends ProductMetadata> {
  purchase: SubscribeOptions<Product>['nativePurchase']
  // Stricter of the two consumers (restore requires Promise<void>); also
  // satisfies subscribeCore, whose closeTransaction accepts void | Promise<void>.
  closeTransaction: RestoreSubscriptionOptions['closeTransaction']
  getAllUnclosedTransactions: RestoreSubscriptionOptions['getAllUnclosedTransactions']
  getAllEntitlements: RestoreSubscriptionOptions['getAllEntitlements']
  // Same shape subscribeCore expects, so it can be threaded straight through.
  logEvent: NonNullable<SubscribeOptions<Product>['logEvent']>
  /**
   * Refreshes native's service level and returns the fresh status. The flow
   * forwards it to `subscribe` as its service-level reader; on success it is
   * fired and forgotten.
   */
  refreshServiceLevel: ReadServiceLevel
}

/** Where the flow reports step failures (e.g. PostHog). Optional per app. */
export interface SubscribeFlowTelemetry {
  captureException: (
    error: unknown,
    context: {
      step: string
      /** The failure code a dialog for this failure shows (ADR 0037). */
      failure_code: string
      purchase_id?: number
      transaction_id?: string
      platform: PaymentPlatform
      product_sku: string
    },
  ) => void
}

export interface SubscribeFlowConfig<Product extends ProductMetadata> {
  api: { service: KyInstance; receipt: KyInstance; infra: KyInstance }
  platform: PaymentPlatform
  native: SubscribeFlowNativeBindings<Product>
  /** When omitted, step failures are not reported (and not renamed). */
  telemetry?: SubscribeFlowTelemetry
  /** Resolves the authenticated user id (any app-specific auth side-effects). */
  getUserId: () => Promise<number>
  getReceiptSystemAccountId: () => string | null
  /** When it returns true, a debug AfterPaymentError is simulated post-payment. */
  getDebugSimulate?: () => boolean
  /**
   * The currently-active subscription's expiry, if any. When provided, both the
   * post-receipt purchase poll and a restore wait for the expiry to change
   * before resolving (so a renewal is not mistaken for the prior
   * subscription). China Android interrupted recovery does not use it: it
   * compares against the baseline `subscribe` reads itself (ADR 0052).
   *
   * Omitting this getter is a guarantee that this flow cannot be opened by an
   * already-subscribed user.
   */
  getCurrentSubscriptionExpiresAt?: () => string | null | undefined
  /**
   * Decides when the post-purchase poll has confirmed the purchase, given the
   * polled status and the purchased product's SKU. When provided, it replaces
   * the built-in predicates (including the `getCurrentSubscriptionExpiresAt`
   * one) — e.g. for a buyer who is already in subscription.
   */
  shouldResolve?: (
    status: ServiceLevelStatus,
    purchasedSku: string,
  ) => status is SubscriptionServiceLevelStatus
  /**
   * An injected post-purchase poll source. When provided, it is polled
   * sequentially with a 1 s gap, failed attempts are skipped, and the 15 s
   * deadline still bounds it. When omitted, the Service API is polled.
   */
  fetchServiceLevelStatus?: FetchServiceLevelStatus
}

/** Per-purchase options for `SubscribeFlow.run`. */
export interface SubscribeRunOptions {
  /** The chosen subscription offer to apply, passed through to `subscribe`. */
  offer?: SubscribeOffer
}

export interface SubscribeFlow<Product extends ProductMetadata> {
  /** Auth + receipt log + the 6-step subscribeCore orchestration + step telemetry. */
  run: (
    product: Product,
    options?: SubscribeRunOptions,
  ) => Promise<SubscriptionServiceLevelStatus | null>
  /** Common success analytics + refreshServiceLevel; returns the derived facts. */
  handleSuccess: (
    status: SubscriptionServiceLevelStatus | null,
    product: Product,
  ) => SuccessFacts
  /** Wraps restoreSubscription with the app's native bindings. */
  restore: () => Promise<SubscriptionServiceLevelStatus | null>
}

/**
 * The single seam each app implements: how it renders a finished purchase and
 * each classified failure. The flow owns the framework-agnostic orchestration;
 * the presenter owns the app-specific UI (dialog store, message catalog) and
 * the success side-effects that vary per app (reminder dialogs, store sets).
 *
 * A consumer wires the two together in a ~15-line framework-native shell:
 *
 * ```ts
 * const flow = createSubscribeFlow(config)
 * const presenter: PurchasePresenter<Product> = { ... }
 *
 * useMutation({
 *   mutationFn: (product) => flow.run(product),
 *   onSuccess: (status, product) =>
 *     presenter.success(flow.handleSuccess(status, product)),
 *   onError: (error) => {
 *     if (error instanceof AbortError) return // a no-op, kept caller-side
 *     presenter.error(classifyPurchaseError(error), { error, flow })
 *   },
 * })
 * ```
 *
 * `AbortError` is deliberately not modelled as a `PurchaseErrorIntent`: it is a
 * no-op the shell short-circuits before classifying. For the
 * `illegal_transaction_restore` intent the presenter drives its restore dialog
 * around `flow.restore()`; `context.error` is provided so the presenter can
 * still capture the raw exception (e.g. the `unknown` intent) and inspect
 * restore-error detail.
 */
export interface PurchasePresenter<
  Product extends ProductMetadata = ProductMetadata,
> {
  success: (facts: SuccessFacts) => void | Promise<void>
  error: (
    intent: PurchaseErrorIntent,
    context: { error: unknown; flow: SubscribeFlow<Product> },
  ) => void | Promise<void>
}

export function createSubscribeFlow<Product extends ProductMetadata>(
  config: SubscribeFlowConfig<Product>,
): SubscribeFlow<Product> {
  const {
    api,
    platform,
    native,
    telemetry,
    getUserId,
    getReceiptSystemAccountId,
    getDebugSimulate,
    getCurrentSubscriptionExpiresAt,
    shouldResolve: shouldResolveForSku,
    fetchServiceLevelStatus,
  } = config

  const run: SubscribeFlow<Product>['run'] = async (product, options) => {
    const userId = await getUserId().catch((error: unknown) => {
      throw tagFailure(error, 'auth')
    })
    const receiptSystemAccountId = getReceiptSystemAccountId()
    void native.logEvent({
      event_name: 'receipt_system_account_id_status',
      event_params: { is_present: String(!!receiptSystemAccountId) },
    })

    const shouldResolve = shouldResolveForSku
      ? bindPurchasedSku(shouldResolveForSku, product.sku)
      : getCurrentSubscriptionExpiresAt
        ? buildShouldResolve(getCurrentSubscriptionExpiresAt())
        : undefined
    const onStepError = telemetry
      ? buildStepTelemetry(telemetry, platform, product.sku)
      : undefined

    return subscribeCore({
      api,
      platform,
      userId,
      product,
      nativePurchase: native.purchase,
      closeTransaction: native.closeTransaction,
      getAllUnclosedTransactions: native.getAllUnclosedTransactions,
      receiptSystemAccountId,
      offer: options?.offer,
      shouldResolve,
      fetchServiceLevelStatus,
      readServiceLevel: native.refreshServiceLevel,
      debugSimulateAfterPaymentError: getDebugSimulate?.(),
      logEvent: native.logEvent,
      onStepError,
    })
  }

  const handleSuccess: SubscribeFlow<Product>['handleSuccess'] = (
    status,
    product,
  ) => {
    void native.logEvent({
      event_name: 'product_sku_purchased_success',
      event_params: { sku_id: product.sku },
    })
    const facts = deriveSuccessFacts(status, product)
    void native.logEvent({
      event_name: facts.isTrialSuccess
        ? 'plus_trial_success'
        : 'plus_subscribe_success',
      event_params: { sku_id: product.sku },
    })
    void native.refreshServiceLevel()
    return facts
  }

  const restore: SubscribeFlow<Product>['restore'] = async () => {
    const userId = await getUserId().catch((error: unknown) => {
      throw tagFailure(error, 'restore_auth')
    })
    return restoreSubscription({
      api,
      userId,
      platform,
      getAllUnclosedTransactions: native.getAllUnclosedTransactions,
      getAllEntitlements: native.getAllEntitlements,
      closeTransaction: native.closeTransaction,
      currentSubscriptionExpiresAt: getCurrentSubscriptionExpiresAt?.(),
    })
  }

  return { run, handleSuccess, restore }
}

function bindPurchasedSku(
  shouldResolve: NonNullable<
    SubscribeFlowConfig<ProductMetadata>['shouldResolve']
  >,
  purchasedSku: string,
): NonNullable<SubscribeOptions<ProductMetadata>['shouldResolve']> {
  return (
    status: ServiceLevelStatus,
  ): status is SubscriptionServiceLevelStatus =>
    shouldResolve(status, purchasedSku)
}

function buildShouldResolve(
  currentSubscriptionExpiresAt: string | null | undefined,
): NonNullable<SubscribeOptions<ProductMetadata>['shouldResolve']> {
  return (
    status: ServiceLevelStatus,
  ): status is SubscriptionServiceLevelStatus =>
    status.service_level === 'subscription' &&
    status.subscription.expires_at !== currentSubscriptionExpiresAt
}

function buildStepTelemetry(
  telemetry: SubscribeFlowTelemetry,
  platform: PaymentPlatform,
  productSku: string,
): NonNullable<SubscribeOptions<ProductMetadata>['onStepError']> {
  return (error, context) => {
    if (
      context.step === 'native_purchase' &&
      error instanceof NativeStoreError &&
      error.message === 'interrupted'
    ) {
      return
    }
    // Read before the rename below changes what the error looks like.
    const failure = readFailureTag(error, 'purchase')
    const failureCode =
      failure.step === 'purchase'
        ? `${context.step}:${failure.cause}`
        : `${failure.step}:${failure.cause}`
    if (error instanceof Error) {
      error.name = 'SubscriptionError'
    }
    telemetry.captureException(error, {
      step: context.step,
      failure_code: failureCode,
      purchase_id: context.purchaseId,
      transaction_id: context.transactionId,
      platform,
      product_sku: productSku,
    })
  }
}
